import { WELL_COLS, WELL_ROWS, idx, type GemKind, type Grid } from '../core/types';
import type { ResolveReport, WaveReport } from '../core/resolve';
import { applyGravity, clearCells } from '../core/grid';

/**
 * How long each wave's cleared cells pulse before they go.
 * The flash is the readable telegraph; without it a cascade is a flicker.
 */
const FLASH_S = 0.26;
/**
 * How long survivors take to fall into the gaps a clear left behind.
 *
 * The cascade is the player's own payoff. Three playtests have now said it is
 * too slow, which is enough signal to stop hedging — it snaps into place, and
 * the readability comes from the flash that precedes it rather than from
 * making the player wait.
 */
const FALL_S = 0.62;
/** Breathing room after everything has landed, before the next wave. */
const SETTLE_S = 0.22;
/**
 * Per-wave decay, so a deep cascade accelerates instead of dragging. Kept
 * gentle: the chain should quicken, not blur.
 */
const FLASH_DECAY = 0.9;
const FALL_DECAY = 0.93;
const SETTLE_DECAY = 0.92;

export function flashFor(wave: number): number {
  return FLASH_S * Math.pow(FLASH_DECAY, Math.max(0, wave - 1));
}
export function fallFor(wave: number): number {
  return FALL_S * Math.pow(FALL_DECAY, Math.max(0, wave - 1));
}
export function settleFor(wave: number): number {
  return SETTLE_S * Math.pow(SETTLE_DECAY, Math.max(0, wave - 1));
}

/** A gem that is dropping into a gap the clear just opened. */
export interface Fall {
  readonly cell: number;
  readonly fromRow: number;
  readonly toRow: number;
  readonly kind: GemKind;
}

export interface Playback {
  /**
   * Board the replay starts from: the state AFTER the card's piece was placed
   * and BEFORE the resolve cleared anything. The piece is already on screen
   * at frame one — it does not fall in from the sky. What animates is what the
   * clear left behind.
   */
  readonly snapshot: Grid;
  /** What the renderer draws. Advances one wave at a time. */
  readonly display: Grid;
  readonly report: ResolveReport;
  /** Wave currently being shown. */
  index: number;
  phase: 'flash' | 'fall' | 'settle' | 'done';
  t: number;
  /** Gems mid-drop for the current wave. Empty when nothing moved. */
  falls: readonly Fall[];
}

/**
 * Which gems move, and from where, when gravity runs.
 *
 * Compaction preserves top-to-bottom order within a column, so the i-th
 * survivor before gravity is the i-th survivor after. Pairing them that way
 * gives an exact from→to map — each gem falls from its own cell to its new
 * one, not from the top of the well. The animation cannot drift from the
 * simulation because it is derived from it.
 */
export function computeFalls(before: Grid, after: Grid): Fall[] {
  const falls: Fall[] = [];
  for (let c = 0; c < WELL_COLS; c++) {
    const from: { row: number; kind: GemKind }[] = [];
    const to: { row: number; kind: GemKind }[] = [];
    for (let r = 0; r < WELL_ROWS; r++) {
      const a = before[idx(r, c)] ?? null;
      if (a !== null) from.push({ row: r, kind: a });
      const b = after[idx(r, c)] ?? null;
      if (b !== null) to.push({ row: r, kind: b });
    }
    for (let i = 0; i < to.length; i++) {
      const f = from[i];
      const t = to[i];
      if (!f || !t) continue;
      if (f.row === t.row) continue;
      falls.push({ cell: idx(t.row, c), fromRow: f.row, toRow: t.row, kind: t.kind });
    }
  }
  return falls;
}

export function startPlayback(snapshot: Grid, report: ResolveReport): Playback | null {
  if (report.waves.length === 0) return null;
  return {
    snapshot: snapshot.slice(),
    display: snapshot.slice(),
    report,
    index: 0,
    phase: 'flash',
    t: 0,
    falls: [],
  };
}

export function tickPlayback(p: Playback, dt: number): void {
  if (p.phase === 'done') return;
  p.t += dt;

  const wave = p.report.waves[p.index];
  const waveNo = wave?.wave ?? 1;

  if (p.phase === 'flash') {
    if (p.t < flashFor(waveNo)) return;
    if (wave) {
      // Clear first, then measure, then gravity — so a gem sitting above a
      // cleared cell knows exactly where it came from.
      clearCells(p.display, wave.cleared);
      const postClear = p.display.slice();
      applyGravity(p.display);
      p.falls = computeFalls(postClear, p.display);
    } else {
      p.falls = [];
    }
    p.phase = p.falls.length > 0 ? 'fall' : 'settle';
    p.t = 0;
    return;
  }

  if (p.phase === 'fall') {
    if (p.t < fallFor(waveNo)) return;
    p.falls = [];
    p.phase = 'settle';
    p.t = 0;
    return;
  }

  if (p.t < settleFor(waveNo)) return;
  p.index += 1;
  if (p.index >= p.report.waves.length) {
    p.phase = 'done';
  } else {
    p.phase = 'flash';
    p.t = 0;
  }
}

export function currentWave(p: Playback): WaveReport | null {
  return p.report.waves[p.index] ?? null;
}

/** Cells pulsing right now — the ones about to clear. */
export function flashingCells(p: Playback): readonly number[] {
  return p.phase === 'flash' ? (currentWave(p)?.cleared ?? []) : [];
}

export function playbackDone(p: Playback): boolean {
  return p.phase === 'done';
}

/** 0→1 through the current fall. */
export function fallProgress(p: Playback): number {
  if (p.phase !== 'fall') return 1;
  const wave = currentWave(p);
  return Math.min(1, p.t / fallFor(wave?.wave ?? 1));
}

export function flashProgress(p: Playback): number {
  if (p.phase !== 'flash') return 0;
  const wave = currentWave(p);
  return Math.min(1, p.t / flashFor(wave?.wave ?? 1));
}

export function playbackDuration(report: ResolveReport): number {
  let total = 0;
  for (const w of report.waves) {
    total += flashFor(w.wave) + FALL_S + settleFor(w.wave);
  }
  return total;
}
